import {Flexbox} from '@/components/ui/flexbox/FlexBox'
import {Grid, GridItem} from '@/components/ui/grid-atoms/GridAtoms'
import {useTranslation} from '@/translations/i18n'
import {useFormContext, useWatch} from 'react-hook-form'
import {InputAddressAutocomplete} from '@/components/commons/input-address-autocomplete/InputAddressAutocomplete'
import {MarkerPin01Icon, MedicalCrossIcon} from '@/components/ui/icon/Icon'
import {Button} from '@/components/ui/button/Button'
import {useEffect, useMemo, useState} from 'react'
import {Coordinates} from '@/types'
import {useGetAslByCoordinates} from '@/services/useGetAslByCoordinates'
import {retrieveAddressDataFromComponents} from '@/features/users/utils'
import {AddressCard} from '@/components/commons/address-card/AddressCard'
import {FormAddressHint, FormCardSection} from '@/components/ui/form-atoms/FormAtoms'
import {PatientResidenceManualFormModal} from '@/features/patients/components/patient-residence-manual-form-modal/PatientResidenceManualFormModal'
import {useAsync} from '@/hooks/useAsync'
import {httpGetMunicipalityByCoordinates} from '@/services/address.http'
import {PatientResidenceFormValidationSchema} from '@/features/patients/types'

export const PatientResidenceForm = ({defaultCoordinates}: {defaultCoordinates?: Coordinates}) => {
    const {t} = useTranslation()
    const [isManualFormModalOpen, setIsManualFormModalOpen] = useState(false)
    const [addressCoordinates, setAddressCoordinates] = useState<Coordinates | null>(defaultCoordinates ?? null)
    const getMunicipality = useAsync()

    // queries
    const aslDistrictQuery = useGetAslByCoordinates({
        coordinates: addressCoordinates ?? undefined,
        responseType: 'extended',
        options: {
            enabled: !!addressCoordinates && !isManualFormModalOpen
        }
    })

    // methods
    const form = useFormContext<PatientResidenceFormValidationSchema>()

    // actions
    const onSelectAddressAutocomplete = (fullAddressValue: google.maps.GeocoderResult | null) => {
        if (fullAddressValue) {
            const {address, city, streetNumber, province, zipCode} = retrieveAddressDataFromComponents(
                fullAddressValue.address_components
            )

            form.setValue('residence.address', address, {shouldDirty: true, shouldValidate: true})
            form.setValue('residence.streetNumber', streetNumber, {shouldDirty: true, shouldValidate: true})
            form.setValue('residence.city', city, {shouldDirty: true, shouldValidate: true})
            form.setValue('residence.province', province, {shouldDirty: true, shouldValidate: true})
            form.setValue('residence.postalCode', zipCode, {shouldDirty: true, shouldValidate: true})
            // clear the previous ASL value before fetching the coordinate-based ASL
            form.setValue('residenceAslDistrict', null, {shouldDirty: true, shouldValidate: true})
            form.clearErrors('residence')

            if (!address || !city || !province || !zipCode) {
                setIsManualFormModalOpen(true)
            }
        }
    }

    // form observer
    const residenceAddress = useWatch({control: form.control, name: 'residence.address'})
    const residenceStreetNumber = useWatch({control: form.control, name: 'residence.streetNumber'})
    const residencePostalCode = useWatch({control: form.control, name: 'residence.postalCode'})
    const residenceCity = useWatch({control: form.control, name: 'residence.city'})
    const residenceProvince = useWatch({control: form.control, name: 'residence.province'})
    const residenceMunicipality = useWatch({control: form.control, name: 'residence.municipality'})
    const residenceAslDistrict = useWatch({control: form.control, name: 'residenceAslDistrict'})

    // actions
    const onLatLngFound = async (coordinates: Coordinates | null) => {
        if (!coordinates) return null

        form.setValue('residence.latitude', coordinates.latitude)
        form.setValue('residence.longitude', coordinates.longitude)

        try {
            const response = await getMunicipality.run(
                httpGetMunicipalityByCoordinates({
                    latitude: coordinates.latitude,
                    longitude: coordinates.longitude
                })
            )

            form.setValue('residence.municipality', response.data)
        } catch (error) {
            console.error('municipality not found', error)
        }

        setAddressCoordinates(coordinates)
    }

    // set initial value
    useEffect(() => {
        if (aslDistrictQuery.data) {
            form.setValue('residenceAslDistrict', aslDistrictQuery.data, {shouldDirty: false})
        }
    }, [aslDistrictQuery.data])

    // trigger changes
    useEffect(() => {
        if (aslDistrictQuery.status == 'success' && residenceAslDistrict?.id != aslDistrictQuery.data.id) {
            form.setValue('residenceAslDistrict', aslDistrictQuery.data, {shouldDirty: true})
        }
        if (aslDistrictQuery.status == 'error' && residenceAslDistrict?.id) {
            form.setValue('residenceAslDistrict', null, {shouldDirty: true})
        }
    }, [aslDistrictQuery?.status])

    // booleans
    const isOutOfRegion = useMemo(
        () =>
            (aslDistrictQuery.isFetchedAfterMount && !residenceAslDistrict?.asl?.id) ||
            (aslDistrictQuery.isError && !residenceAslDistrict?.asl?.id),
        [aslDistrictQuery, residenceAslDistrict?.asl?.id]
    )

    return (
        <Flexbox direction="column" gap={4} fullWidth>
            <Grid columns={6} columnGap={2} rowGap={4} fullWidth>
                <GridItem startColumn={1} endColumn={4}>
                    <InputAddressAutocomplete
                        label={t('commons:residence_address')}
                        placeholder={residenceAddress && t('users:search_new_address_placeholder')}
                        onLatLngFound={onLatLngFound}
                        errorMessage={form.formState.errors.residence?.message}
                        onChange={onSelectAddressAutocomplete}
                        showManualForm={() => setIsManualFormModalOpen(true)}
                        predictionTypes={['address']}
                    />
                </GridItem>
            </Grid>

            {residenceAddress && (
                <>
                    <FormCardSection columns={6} fullWidth>
                        <GridItem startColumn={1} endColumn={4}>
                            <AddressCard
                                icon={<MarkerPin01Icon size={20} />}
                                kicker={t('commons:address')}
                                title={`${residenceAddress}${residenceStreetNumber && `, ${residenceStreetNumber}`}`}
                                subtitle={
                                    residenceMunicipality
                                        ? `${residencePostalCode} ${residenceCity?.name} - ${residenceMunicipality.name}`
                                        : `${residencePostalCode} ${residenceCity?.name} (${residenceProvince?.acronym})`
                                }
                                border="right"
                            />
                        </GridItem>
                        <GridItem startColumn={4} endColumn={7}>
                            <AddressCard
                                icon={<MedicalCrossIcon size={20} />}
                                kicker={t('commons:asl')}
                                title={isOutOfRegion ? t('commons:out_region') : residenceAslDistrict?.asl?.name}
                                subtitle={
                                    isOutOfRegion ? t('commons:asl_location_not_found') : residenceAslDistrict?.name
                                }
                                isLoading={aslDistrictQuery.isFetching || getMunicipality.isLoading}
                                isError={isOutOfRegion}
                            />
                        </GridItem>
                    </FormCardSection>
                    <FormAddressHint gap={1}>
                        {t('commons:residence_hint_label')}
                        <Button
                            size="xs"
                            variant={form.formState.errors.residence ? 'secondaryDanger' : 'tertiary'}
                            shape="text"
                            onClick={() => setIsManualFormModalOpen(true)}
                        >
                            {t('commons:edit_fields')}
                        </Button>
                    </FormAddressHint>
                </>
            )}

            {isManualFormModalOpen && (
                <PatientResidenceManualFormModal
                    onClose={() => setIsManualFormModalOpen(false)}
                    defaultValues={{
                        residence: {
                            address: residenceAddress,
                            city: residenceCity,
                            municipality: residenceMunicipality,
                            streetNumber: residenceStreetNumber,
                            province: residenceProvince,
                            postalCode: residencePostalCode
                        },
                        residenceAslDistrict
                    }}
                />
            )}
        </Flexbox>
    )
}
